import { useGetMe } from 'api-hooks/auth/query';
import { CustomerLiteModel, CustomerModel } from 'api-hooks/customer';
import { GuestLiteModel, GuestModel } from 'api-hooks/guest';
import { PreferenceTagItemModel } from 'api-hooks/preference-tag';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { FlightCheckInStatusEnum } from 'common/constants/enum';
import { distinct } from 'common/utils/iterable';
import { capitalize, generateId } from 'common/utils/string';
import { generateUniqueAirlineItem } from 'legacy-modules/order/ticket-order/hooks/use-get-default-values';
import React from 'react';

import {
  TicketSaleFlightAddOnsFormType,
  TicketSaleFlightAirlineFormType,
  TicketSaleFlightGuestFormType,
  TicketSaleFlightSaleFormType,
  TicketSaleFormType,
} from '../components/form-type';

export function guestNameTransformer(guest: GuestModel | GuestLiteModel) {
  return `${capitalize(guest.title)} ${guest.name}`;
}

export function customerNameTransformer(
  customer: CustomerModel | CustomerLiteModel,
) {
  return `${capitalize(customer.title)} ${customer.name}`;
}

export function guestPreferencesTransformer(
  guest: GuestModel | GuestLiteModel,
) {
  const preferenceStats = guest.preferenceStats.map((ps) => {
    return {
      id: ps.id,
      label: `${ps.name} (${ps.count}X)`,
    };
  });

  const guestPreferences = guest.preferences.map((ps) => {
    return {
      id: ps.id,
      label: ps.name,
    };
  });

  return [...preferenceStats, ...guestPreferences];
}

export function preferencesTransformer(preferences: PreferenceTagItemModel[]) {
  return preferences.map((preference) => {
    return { id: preference.id, label: preference.name };
  });
}

export function addNewAddOnsItem(): TicketSaleFlightAddOnsFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
    route: '',
  } as never as TicketSaleFlightAddOnsFormType;
}
export function addNewAirlineItem(): TicketSaleFlightAirlineFormType {
  return {
    customId: generateId(),
    id: undefined,
    airlineId: '',
    airlineName: '',
    date: undefined,
    detail: '',
    preferenceId: '',
    price: undefined,
    route: '',
    time: '',
    checkInStatus: FlightCheckInStatusEnum.NotCheckedIn,
  } as never as TicketSaleFlightAirlineFormType;
}

export function addNewGuestItem(): TicketSaleFlightGuestFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    guestPreferences: [],
    id: undefined,
    preferences: [],
    ticket: '',
  };
}

export function addNewFlightItem(): TicketSaleFlightSaleFormType {
  return {
    bookingCode: '',
    customId: generateId(),
    flightSaleAddOns: [],
    flightSaleAirlines: [addNewAirlineItem()],
    flightSaleGuests: [addNewGuestItem()],
    subtotalAddOns: 0,
    subtotalAirlines: 0,
  };
}

interface Props {
  ticketSale?: TicketSaleModel | undefined;
  defaultValues?: TicketSaleFormType;
}

export default function useGetDefaultValues(props: Props) {
  const { ticketSale } = props;
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;

  const defaultValues = React.useMemo<TicketSaleFormType>(() => {
    const transactionAt = ticketSale?.transactionAt ?? new Date();

    if (props.defaultValues) {
      return {
        ...props.defaultValues,
        transactionAt,
        tempTransactionAt: transactionAt,
        refId: undefined,
        ticketSale: undefined,
        flightSales: props.defaultValues.flightSales.map((fs) => {
          return {
            ...fs,
            id: undefined,
            flightSaleAddOns: fs.flightSaleAddOns.map((fsao) => {
              return {
                ...fsao,
                id: undefined,
              };
            }),
            flightSaleAirlines: fs.flightSaleAirlines.map((fsa) => {
              return {
                ...fsa,
                id: undefined,
              };
            }),
            flightSaleGuests: fs.flightSaleGuests.map((fsg) => {
              return {
                ...fsg,
                id: undefined,
              };
            }),
          };
        }),
      };
    }

    const flightSales: TicketSaleFlightSaleFormType[] =
      ticketSale?.flightSales?.map((fs) => {
        const flightSaleAddOns: TicketSaleFlightAddOnsFormType[] =
          fs.flightSaleAddOns.map((fsao) => {
            return {
              customId: generateId(),
              description: fsao.description || '',
              guestId: fsao.guest.id,
              guestName: guestNameTransformer(fsao.guest),
              id: fsao.id,
              price: fsao.price || 0,
              route: fsao.route || '',
            };
          });

        const flightSaleGuests: TicketSaleFlightGuestFormType[] = distinct(
          fs.flightSaleGuests,
          (fsg) => fsg.guest.id,
        ).map((fsg) => {
          const guest = fsg.guest;
          return {
            customId: generateId(),
            guestId: guest.id,
            guestName: guestNameTransformer(guest),
            guestPreferences: guestPreferencesTransformer(guest),
            id: fsg.id,
            preferences: fsg.preferences.map((fsgp) => {
              return { id: fsgp.id, label: fsgp.name };
            }),
            ticket: fsg.ticket || '',
          };
        });

        const flightSaleAirlines: TicketSaleFlightAirlineFormType[] = distinct(
          fs.flightSaleGuests,
          (fsg) => generateUniqueAirlineItem(fsg),
        ).map((fsg) => {
          return {
            customId: generateId(),
            airlineId: fsg.airline.id,
            airlineName: fsg.airline.name,
            date: fsg.date,
            detail: fsg.detail || '',
            preferenceId: fsg.preference?.id || '',
            price: fsg.price || 0,
            route: fsg.route || '',
            time: fsg.time || '',
            id: undefined,
            checkInStatus: fsg.checkInStatus || null,
          };
        });

        const subtotalAddOns = flightSaleAddOns.reduce(
          (prev, fsao) => prev + (fsao.price || 0),
          0,
        );
        const subtotalAirlines =
          flightSaleAirlines.reduce((prev, fsa) => prev + (fsa.price || 0), 0) *
          flightSaleGuests.length;

        return {
          id: fs.id,
          bookingCode: fs.bookingCode || '',
          customId: generateId(),
          flightSaleAddOns,
          flightSaleAirlines,
          flightSaleGuests,
          subtotalAddOns,
          subtotalAirlines,
        };
      }) ?? [addNewFlightItem()];

    return {
      address: ticketSale?.customer.address || '',
      adminId: ticketSale?.admin.id ?? employeeHook?.id ?? '',
      code: ticketSale?.code || '',
      customerBillingId: ticketSale?.customerBillingAddress?.id ?? '',
      customerBillingOptions: [],
      customerId: ticketSale?.customer.id ?? '',
      customerStatus: ticketSale?.customer.status ?? '',
      hasDueReceivable: ticketSale?.customer.hasDueReceivables ?? false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      description: ticketSale?.description || '',
      discount: ticketSale?.discount ?? 0,
      needApproval: false,
      number: ticketSale?.invoiceNumber ?? '',
      operatorId: ticketSale?.operator?.id ?? '',
      otherCost: ticketSale?.otherCost ?? 0,
      phone: ticketSale?.customer.phone ?? '',
      refId: ticketSale?.refId,
      parentRefId: ticketSale?.parentSale?.refId,
      salesmanId: ticketSale?.salesman?.id ?? '',
      total: ticketSale?.total ?? 0,
      transactionAt,
      tempTransactionAt: transactionAt,
      flightSales,
      ticketSale,
    };
  }, [employeeHook?.id, props.defaultValues, ticketSale]);

  return defaultValues;
}
